import { useEffect, useState, type ReactNode } from "react"
import { ArrowLeft, LoaderCircle, LogOut } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { BrandLogo } from "@/components/brand-logo"
import { ThemeToggle } from "@/components/theme-toggle"
import { api, type AuthUser } from "@/lib/api"
import { cn } from "@/lib/utils"

function initialsOf(name: string, email: string) {
  const fromName = name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("")
  if (fromName) return fromName
  return (email.trim()[0]?.toUpperCase() ?? "?") + (email.trim()[1]?.toUpperCase() ?? "")
}

type AppShellProps = {
  children: ReactNode
  title?: ReactNode
  badge?: ReactNode
  description?: string
  backTo?: string
  actions?: ReactNode
}
export function AppShell({ children, title, badge, description: _description, backTo, actions }: AppShellProps) {
  const navigate = useNavigate()
  const [loggingOut, setLoggingOut] = useState(false)
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)

  useEffect(() => {
    let cancelled = false
    api.me().then((user) => { if (!cancelled) setCurrentUser(user) }).catch(() => { if (!cancelled) setCurrentUser(null) })
    return () => { cancelled = true }
  }, [])

  async function handleLogout() {
    setLoggingOut(true)
    try { await api.logout() } catch { /* A navegação continua mesmo se a API falhar. */ }
    finally { navigate("/login", { replace: true }) }
  }

  const editor = Boolean(backTo)
  return (
    <div className={cn("bg-background text-foreground font-sans", editor ? "h-screen flex flex-col overflow-hidden" : "min-h-screen overflow-hidden")}>
      <header className={cn("relative z-20 shrink-0 border-b border-border bg-card", editor ? "h-[76px]" : "h-16")}>
        <div className="flex h-full w-full items-center justify-between px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {editor ? (
              <div className="flex items-center gap-3">
                <Button asChild variant="ghost" size="sm" className="h-9 gap-1.5 rounded-lg px-2 text-[13px] font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground">
                  <Link to={backTo ?? "/#overview"}>
                    <ArrowLeft className="size-4" />
                    <span className="hidden sm:inline">Painel</span>
                  </Link>
                </Button>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="truncate font-display text-[15px] font-bold text-foreground">
                    {title ?? "Editor de assistente"}
                  </span>
                  {badge}
                </div>
              </div>
            ) : (
              <>
                <Link to="/" className="group flex items-center gap-3" aria-label="Painel do BubbleDesk">
                  <BrandLogo size="md" className="text-foreground" />
                </Link>
                <div className="hidden items-center gap-2 border-l border-border pl-3 lg:flex">
                  <span className="text-[13px] font-semibold text-foreground">Assistentes</span>
                  <span className="text-[12px] text-muted-foreground">/ Workspace</span>
                </div>
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {!editor && (
              <div className="hidden items-center gap-2 rounded-full border border-border px-2.5 py-1 sm:flex">
                <span className="size-2 rounded-full bg-success" />
                <span className="text-[11px] font-medium text-muted-foreground">Workspace operacional</span>
              </div>
            )}
            <ThemeToggle />
            {editor ? (
              actions
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-border px-2 py-1 transition hover:bg-muted/40" title={currentUser ? `${currentUser.name} · ${currentUser.email}${currentUser.role === "superadmin" ? " · Superadmin" : ""}` : "Conta"}>
                <Avatar className="size-6 border-0 bg-primary">
                  <AvatarFallback className="bg-primary text-[10px] font-semibold text-primary-foreground">{currentUser ? initialsOf(currentUser.name, currentUser.email) : "…"}</AvatarFallback>
                </Avatar>
                <span className="hidden max-w-[140px] truncate text-[12px] font-semibold sm:block">{currentUser ? currentUser.name : "Conta"}</span>
                {currentUser?.role === "superadmin" && <span className="hidden rounded-full bg-info/10 px-1.5 py-0.5 text-[10px] font-semibold text-info md:block">Superadmin</span>}
                <button
                  type="button"
                  aria-label="Sair"
                  title="Sair"
                  className="ml-1 text-muted-foreground hover:text-destructive"
                  onClick={() => void handleLogout()}
                  disabled={loggingOut}
                >
                  {loggingOut ? <LoaderCircle className="size-3.5 animate-spin" /> : <LogOut className="size-3.5" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className={cn(editor ? "h-[calc(100vh-4.75rem)] min-h-0 flex-1 overflow-hidden" : "")}>{children}</main>
    </div>
  )
}
